// Quotation (presupuesto) list: search, status filter, pipeline totals, and the
// row actions — open, PDF, email, convert to invoice, delete.

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync, useDebounced, useErrorMessage } from '../lib/hooks';
import { useI18n, useT } from '../lib/i18n';
import { useSession } from '../lib/session';
import { useToast } from '../components/Toast';
import { Icon } from '../components/Icon';
import { EmailDialog } from '../components/EmailDialog';
import {
  ConfirmDialog, EmptyState, ErrorAlert, LoadingRow, Pagination, SearchInput,
} from '../components/ui';
import { formatDate, formatMoney } from '@shared/format';
import { QUOTATION_STATUSES, type QuotationStatus, type QuotationSummary } from '@shared/types';

const PER_PAGE = 20;

/** Expiry is reported by the server; a live quotation shows its own status. */
export function QuotationBadge({ quotation }: { quotation: QuotationSummary }) {
  const t = useT();
  if (quotation.isExpired) {
    return <span className="badge badge--neutral">{t('status.expired')}</span>;
  }
  const tone = quotation.status === 'accepted' ? 'paid'
    : quotation.status === 'rejected' ? 'neutral'
    : quotation.status === 'sent' ? 'accent' : 'pending';
  return (
    <span className={`badge badge--${tone}`}>
      {t(`status.${quotation.status}` as const)}
    </span>
  );
}

export function Quotations() {
  const t = useT();
  const { locale } = useI18n();
  const { money } = useSession();
  const toast = useToast();
  const navigate = useNavigate();
  const toMessage = useErrorMessage();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<QuotationStatus | ''>('');
  const [page, setPage] = useState(1);
  const [pendingDelete, setPendingDelete] = useState<QuotationSummary>();
  const [pendingConvert, setPendingConvert] = useState<QuotationSummary>();
  const [emailing, setEmailing] = useState<QuotationSummary>();
  const [busy, setBusy] = useState(false);

  const q = useDebounced(search);
  const { data, loading, error, reload } = useAsync(
    () => api.quotations({ q, status: status || undefined, page, perPage: PER_PAGE }),
    [q, status, page]);

  const currency = (value: number) => formatMoney(value, money);

  async function confirmDelete() {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      const { number } = await api.deleteQuotation(pendingDelete.id);
      toast.success(t('quotations.deleted', { number }));
      setPendingDelete(undefined);
      reload();
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function confirmConvert() {
    if (!pendingConvert) return;
    setBusy(true);
    try {
      const result = await api.convertQuotation(pendingConvert.id, 'transfer');
      toast.success(t('quotation.converted', { number: result.invoiceNumber }));
      setPendingConvert(undefined);
      navigate(`/invoices/${result.invoiceId}`);
    } catch (err) {
      toast.error(toMessage(err));
      setBusy(false);
    }
  }

  const totals = data?.totals;

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-head__title">{t('quotations.title')}</h1>
          <p className="page-head__subtitle">{t('quotations.emptyHint')}</p>
        </div>
        <div className="page-head__actions">
          <Link to="/quotations/new" className="btn btn--primary">
            <Icon name="plus" />{t('quotations.new')}
          </Link>
        </div>
      </div>

      {totals && totals.count > 0 && (
        <div className="stats">
          <div className="stat">
            <div className="stat__label">{t('quotations.totals.quotations')}</div>
            <div className="stat__value">{totals.count}</div>
          </div>
          <div className="stat">
            <div className="stat__label">{t('status.draft')}</div>
            <div className="stat__value stat__value--muted">{totals.draftCount}</div>
          </div>
          <div className="stat">
            <div className="stat__label">{t('status.sent')}</div>
            <div className="stat__value stat__value--muted">{totals.sentCount}</div>
          </div>
          <div className="stat">
            <div className="stat__label">{t('quotations.totals.accepted')}</div>
            <div className="stat__value">{totals.acceptedCount}</div>
          </div>
          <div className="stat">
            <div className="stat__label">{t('quotations.totals.open')}</div>
            <div className={`stat__value${totals.openValue > 0 ? '' : ' stat__value--muted'}`}>
              {currency(totals.openValue)}
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card__head">
          <div className="toolbar" style={{ flex: 1 }}>
            <SearchInput value={search} onChange={(v) => { setSearch(v); setPage(1); }}
              placeholder={t('quotations.searchPlaceholder')} />
            <select className="select" style={{ width: 'auto' }} value={status}
              onChange={(e) => { setStatus(e.target.value as QuotationStatus | ''); setPage(1); }}
              aria-label={t('common.status')}>
              <option value="">{t('common.all')}</option>
              {QUOTATION_STATUSES.map((s) => (
                <option key={s} value={s}>{t(`status.${s}` as const)}</option>
              ))}
            </select>
            {loading && data && <span className="spinner" />}
          </div>
        </div>

        {error ? <div className="card__body"><ErrorAlert message={error} onRetry={reload} /></div>
          : loading && !data ? <LoadingRow />
          : data && data.items.length === 0 ? (
            <EmptyState
              icon={q || status ? 'search' : 'file-plus'}
              title={q || status ? t('common.noResults') : t('quotations.empty')}
              hint={q || status ? t('common.noResultsHint') : t('quotations.emptyHint')}
              action={!q && !status && (
                <Link to="/quotations/new" className="btn btn--primary">
                  <Icon name="plus" />{t('quotations.new')}
                </Link>
              )}
            />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th className="shrink">{t('quotations.number')}</th>
                    <th className="shrink">{t('invoices.date')}</th>
                    <th className="shrink">{t('quotations.validUntil')}</th>
                    <th>{t('invoices.client')}</th>
                    <th>{t('invoices.seller')}</th>
                    <th className="shrink">{t('common.status')}</th>
                    <th className="num">{t('common.total')}</th>
                    <th className="shrink" />
                  </tr>
                </thead>
                <tbody>
                  {data?.items.map((quotation) => (
                    <tr key={quotation.id}>
                      <td className="shrink mono">{quotation.number}</td>
                      <td className="shrink nowrap">{formatDate(quotation.issuedAt, locale)}</td>
                      <td className="shrink nowrap table__meta">{formatDate(quotation.validUntil, locale)}</td>
                      <td>
                        <Link to={`/quotations/${quotation.id}`} className="table__primary">
                          {quotation.clientName}
                        </Link>
                        <div className="table__meta truncate">
                          {quotation.convertedInvoiceNumber
                            ? t('quotation.convertedBanner', { number: quotation.convertedInvoiceNumber })
                            : quotation.lastEmailedAt
                              ? t('email.lastSent', { date: formatDate(quotation.lastEmailedAt, locale) })
                              : [quotation.clientPhone, quotation.clientEmail].filter(Boolean).join(' · ')}
                        </div>
                      </td>
                      <td className="table__meta">{quotation.sellerName}</td>
                      <td className="shrink"><QuotationBadge quotation={quotation} /></td>
                      <td className="num">
                        <div className="table__primary">{currency(quotation.total)}</div>
                        <div className="table__meta">{t('invoices.lines')}: {quotation.lineCount}</div>
                      </td>
                      <td className="shrink">
                        <div className="table__actions">
                          <button type="button" className="btn btn--ghost btn--icon"
                            title={t('common.edit')} aria-label={t('common.edit')}
                            onClick={() => navigate(`/quotations/${quotation.id}`)}>
                            <Icon name="edit" />
                          </button>
                          <button type="button" className="btn btn--ghost btn--icon"
                            title={t('email.send')} aria-label={t('email.send')}
                            onClick={() => setEmailing(quotation)}>
                            <Icon name="mail" />
                          </button>
                          <a className="btn btn--ghost btn--icon"
                            href={api.quotationPdfUrl(quotation.id, locale)}
                            title={t('quotations.download')} aria-label={t('quotations.download')}>
                            <Icon name="download" />
                          </a>
                          <button type="button" className="btn btn--ghost btn--icon"
                            title={t('quotation.convert')} aria-label={t('quotation.convert')}
                            disabled={quotation.convertedInvoiceId !== null}
                            onClick={() => setPendingConvert(quotation)}>
                            <Icon name="arrow-right-circle" />
                          </button>
                          <button type="button" className="btn btn--ghost btn--icon"
                            title={t('common.delete')} aria-label={t('common.delete')}
                            onClick={() => setPendingDelete(quotation)}>
                            <Icon name="trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        {data && data.items.length > 0 && (
          <div className="card__foot">
            <Pagination page={data.page} totalPages={data.totalPages} total={data.total}
              perPage={data.perPage} onChange={setPage} />
          </div>
        )}
      </div>

      {emailing && (
        <EmailDialog
          kind="quotation"
          id={emailing.id}
          documentLabel={`${t('quotations.title')} ${emailing.number} · ${emailing.clientName}`}
          onClose={() => setEmailing(undefined)}
          onSent={reload}
        />
      )}

      {pendingConvert && (
        <ConfirmDialog
          title={t('quotation.convert')}
          message={t('quotation.convertConfirm', { number: pendingConvert.number })}
          confirmLabel={t('quotation.convert')}
          busy={busy}
          onConfirm={() => void confirmConvert()}
          onCancel={() => setPendingConvert(undefined)}
        />
      )}

      {pendingDelete && (
        <ConfirmDialog
          title={t('common.delete')}
          message={t('quotations.deleteConfirm', { number: pendingDelete.number })}
          confirmLabel={t('common.delete')}
          danger
          busy={busy}
          onConfirm={() => void confirmDelete()}
          onCancel={() => setPendingDelete(undefined)}
        />
      )}
    </>
  );
}
